/**
 * bs-klaviyo-consent-form.js — opens the Klaviyo Fanclub popup only after the
 * cookie banner is out of the way.
 *
 * Problem this solves: the Klaviyo form used its own time trigger (15s from
 * page load). Shopify's consent banner stays up until the visitor decides, so
 * anyone who hesitated got the discount popup stacked on top of the banner —
 * on mobile that covered the whole viewport and neither could be read.
 *
 * Instead the form is opened from here, keyed to the consent decision:
 *   - banner still open  → wait for `visitorConsentCollected`, then a short delay
 *   - already decided    → open after a short delay (no banner is in the way)
 *
 * Also opens after a *declined* banner: the signup is an email opt-in with its
 * own double opt-in, not a cookie, so cookie consent is not what gates it. Note
 * that Klaviyo's onsite script itself may be consent-gated in the app embed —
 * if it is, `openForm` is a no-op for those visitors and this cannot change it.
 *
 * The Klaviyo form itself keeps a long fallback delay in its own trigger rules,
 * so signups do not die silently if the consent API never loads here.
 *
 * `openForm` bypasses Klaviyo's own display rules, so the "already submitted"
 * and "wait 3 days after close" suppressions are mirrored locally off the
 * `klaviyoForms` events. Without that, subscribers would see the popup again.
 *
 * Debug output: append `?bs_klaviyo=debug` to the URL.
 */
(function () {
  "use strict";

  var FORM_ID = "Te2awt"; // Klaviyo "Fanclub_Form"
  var DELAY_AFTER_CONSENT_MS = 15000; // banner just closed, let the page breathe
  var DELAY_IF_DECIDED_MS = 8000; // returning visitor, no banner was in the way
  var REOPEN_AFTER_CLOSE_MS = 3 * 24 * 60 * 60 * 1000; // mirrors Klaviyo's 3 days
  var STORAGE_KEY = "bs_klaviyo_fanclub";

  var DEBUG = /[?&]bs_klaviyo=debug/.test(window.location.search);
  var opened = false;

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[bs-klaviyo]");
    console.log.apply(console, args);
  }

  /* --- local mirror of Klaviyo's suppression rules ------------------------ */

  function readState() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {}; // private mode / storage disabled → treat as first visit
    }
  }

  function writeState(patch) {
    try {
      var next = readState();
      for (var key in patch) next[key] = patch[key];
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* nothing we can do, and nothing that should break the page */
    }
  }

  function suppressed() {
    var state = readState();
    if (state.submitted) return "already submitted";
    if (state.closedAt && Date.now() - state.closedAt < REOPEN_AFTER_CLOSE_MS) {
      return "closed less than 3 days ago";
    }
    return null;
  }

  window.addEventListener("klaviyoForms", function (event) {
    if (!event.detail || event.detail.formId !== FORM_ID) return;
    if (event.detail.type === "submit") writeState({ submitted: true });
    if (event.detail.type === "close") writeState({ closedAt: Date.now() });
  });

  /* --- opening ----------------------------------------------------------- */

  function openForm() {
    if (opened) return;
    var reason = suppressed();
    if (reason) {
      log("not opening:", reason);
      return;
    }
    opened = true;
    log("opening form");
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(["openForm", FORM_ID]);
  }

  function openFormAfter(ms) {
    if (suppressed()) return; // save the timer when we already know the answer
    log("scheduled in", ms, "ms");
    window.setTimeout(openForm, ms);
  }

  /* --- consent ----------------------------------------------------------- */

  // Fires whenever the visitor answers the banner, accept or decline alike —
  // either way the banner is gone and the screen is free.
  document.addEventListener("visitorConsentCollected", function (event) {
    log("consent collected, marketing:", event.detail && event.detail.marketingAllowed);
    openFormAfter(DELAY_AFTER_CONSENT_MS);
  });

  // Returning visitors never see the banner, so no event fires for them —
  // ask the API for the decision they made earlier.
  function checkExistingConsent() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) {
      log("consent API unavailable, leaving it to Klaviyo's fallback delay");
      return;
    }
    try {
      if (privacy.shouldShowBanner()) {
        log("banner is up, waiting for the decision");
        return;
      }
      log("decision already on file, nothing blocking the screen");
      openFormAfter(DELAY_IF_DECIDED_MS);
    } catch (e) {
      log("consent API threw", e);
    }
  }

  if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (error) {
        if (error) {
          log("could not load consent-tracking-api", error);
          return;
        }
        checkExistingConsent();
      }
    );
  } else {
    log("Shopify.loadFeatures missing, nothing to key off");
  }
})();
